import webApiClient from '@tunein/web-api-client-legacy';
import { pick } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';
import { apiBeta } from 'src/client/api';
import appConfig from 'src/common/config';
import {
  CLIENT_API_RETRY_LIMIT,
  CLIENT_API_TIMEOUT,
} from 'src/common/constants/experiments/config';
import {
  UNIFIED_EVENTS_BATCH_INTERVAL_SECS,
  UNIFIED_EVENTS_MAX_BATCH_COUNT,
} from 'src/common/constants/experiments/reporting';
import { selectUserIpAddress } from 'src/common/selectors/app';
import { selectIsUserSubscribed } from 'src/common/selectors/me';
import getConfigStreamFormats from 'src/common/utils/getConfigStreamFormats';
import { configApiOptions } from '../../common/config/configApiOptions';
import { getBaseApiConfig } from '../../common/config/utils';
import {
  PARTNER_USERNAME,
  PARTNER_USERNAME_EXTERNAL_DEMO,
} from '../../common/constants/experiments/partners';
import { FORCE_HTTPS_STREAMS } from '../../common/constants/experiments/player';
import { getGuildIdQueryParam } from '../../common/utils/discord';
import { isDevBuild } from '../../common/utils/environment';

export default function createTuneInApiClient(initialState = {}) {
  const { auth, config, app = {} } = initialState;
  const authTokens = pick(auth?.tokenInfo || {}, ['accessToken', 'refreshToken']);
  const rmConfigs = config?.autoupdate || {};
  const {
    isMobile,
    isDesktop,
    isDiscord,
    isFord,
    isFordSubdomain,
    desktopVersion,
    tuneInUserSerial,
    partnerId,
    userAgent,
  } = app;

  const apiConfig = {
    ...getBaseApiConfig(isDiscord, isFord),
    configApiOptions: {
      ...configApiOptions,
      ...(isFord && { commands: appConfig.autoConfigCommands }),
    },
    partnerId,
    serial: tuneInUserSerial,
    streamFormats: getConfigStreamFormats(isMobile, isDesktop, isDiscord, userAgent),
    ...(desktopVersion && { desktopVersion }),
    ...(isDiscord && { productMode: getGuildIdQueryParam() }),
    ...(isFord && {
      username: rmConfigs[isFordSubdomain ? PARTNER_USERNAME_EXTERNAL_DEMO : PARTNER_USERNAME],
    }),
    graphQlTransformAllowlist: { getUser: true },

    // Config-based options -- new API client accesses these internally
    timeout: rmConfigs[CLIENT_API_TIMEOUT],
    retryLimit: rmConfigs[CLIENT_API_RETRY_LIMIT],
    reportingIntervalDelaySecs: rmConfigs[UNIFIED_EVENTS_BATCH_INTERVAL_SECS],
    reportingMaxBatch: rmConfigs[UNIFIED_EVENTS_MAX_BATCH_COUNT],
    forceHttpsStreams: rmConfigs[FORCE_HTTPS_STREAMS],

    // misc
    debug: isDevBuild(),

    // Sync legacy WAC auth tokens to new WAC
    onSetNewTokens: (_apiContext, authTokens) => {
      apiBeta.authTokens = authTokens;
    },
  };

  // TODO: disabled per TUNE-17244 - revisiting this when it won't cause our URLs to be translated
  // const requestHeaders = {
  //   ...(acceptLanguageHeader && { 'Accept-Language': acceptLanguageHeader }),
  // };
  const client = webApiClient(apiConfig);

  if (!isEmpty(authTokens)) {
    client.setTokens(authTokens);
  }

  apiBeta.init({
    ...getBaseApiConfig(isDiscord, isFord),
    configServiceOptions: configApiOptions,
    uiServiceOptions: {
      generateRoutePathForGuideItem: (guideItem) => {
        return guideItem.seoInfo?.cleanPath || guideItem.destinationInfo?.cleanPath || '';
      },
    },
    configData: config,
    partnerId,
    serial: tuneInUserSerial,
    authTokens,
    optionalParams: {
      formats: getConfigStreamFormats(isMobile, isDesktop, isDiscord, userAgent),
      desktopVersion,
      ...(isDiscord && { productMode: getGuildIdQueryParam() }),
    },
    debug: isDevBuild(),
    isPremiumOverride: selectIsUserSubscribed(initialState),
    // We have a suspicion that the UE API endpoint doesn't resolve IP/geolocation from the X-Forwarded-For headers like other Platform APIs,
    // so we can't use navigator.beacon() to proxy these reports through the server until that feature is implemented, if ever
    sendScreenViewReportsAsBeacon: false,
    ipAddress: selectUserIpAddress(initialState),
  });

  // Sync new WAC auth tokens to legacy WAC
  apiBeta.on('authTokensUpdated', (authTokens) => {
    if (authTokens.accessToken) {
      // WAC legacy's setTokens() only emits a token event if we pass in `true` as the second argument
      client.setTokens(authTokens);
      return;
    }

    // Avoids a loop, since both WACs emit an event when tokens are updated
    if (client.getTokens()?.accessToken) {
      client.clearTokens();
    }
  });

  return client;
}
